import { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Upload, X, FileText, Download, Eye, FolderOpen, Filter } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/axios';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { openS3File, downloadS3File } from '@/lib/storage';
import { getErrorMessage } from '@/lib/errors';

const inputCls = 'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 bg-white';

const CATEGORIAS = [
  { value: '', label: 'Todas' },
  { value: 'laboratorio', label: 'Laboratorio' },
  { value: 'imagenologia', label: 'Imagenología' },
  { value: 'receta', label: 'Receta' },
  { value: 'consentimiento', label: 'Consentimiento' },
  { value: 'identificacion', label: 'Identificación' },
  { value: 'referencia', label: 'Referencia' },
  { value: 'otro', label: 'Otro' },
];

const CATEGORIA_COLOR: Record<string, string> = {
  laboratorio: 'bg-blue-50 text-blue-600 border-blue-100',
  imagenologia: 'bg-indigo-50 text-indigo-600 border-indigo-100',
  receta: 'bg-green-50 text-green-600 border-green-100',
  consentimiento: 'bg-yellow-50 text-yellow-700 border-yellow-100',
  identificacion: 'bg-orange-50 text-orange-600 border-orange-100',
  referencia: 'bg-purple-50 text-purple-600 border-purple-100',
  otro: 'bg-gray-100 text-gray-600 border-gray-200',
};

const ICON_BY_MIME: Record<string, string> = {
  'application/pdf': '📄',
  'image/': '🖼',
  'application/msword': '📝',
  'application/vnd.openxmlformats-officedocument': '📝',
  'audio/': '🎵',
  'video/': '🎬',
};

function fileIcon(mime: string) {
  for (const [prefix, icon] of Object.entries(ICON_BY_MIME)) {
    if (mime?.startsWith(prefix)) return icon;
  }
  return '📎';
}

function fmtBytes(bytes: number) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function fmtDate(iso: string) {
  try { return format(new Date(iso), "d MMM yyyy", { locale: es }); }
  catch { return iso; }
}

interface UploadModalProps {
  onClose: () => void;
  onUpload: (fd: FormData) => void;
  isPending: boolean;
}

function UploadModal({ onClose, onUpload, isPending }: UploadModalProps) {
  const [file, setFile] = useState<File | null>(null);
  const [nombre, setNombre] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [categoria, setCategoria] = useState('otro');
  const dropRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    const f = e.dataTransfer.files[0];
    if (f) { setFile(f); setNombre(prev => prev || f.name); }
  }

  function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (f) { setFile(f); setNombre(prev => prev || f.name); }
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!file) return toast.error('Selecciona un archivo');
    const fd = new FormData();
    fd.append('file', file);
    fd.append('nombre', nombre || file.name);
    if (descripcion) fd.append('descripcion', descripcion);
    fd.append('categoria', categoria);
    onUpload(fd);
  }

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h3 className="font-semibold text-text-primary">Subir documento</h3>
          <button type="button" onClick={onClose} className="p-1 rounded-lg hover:bg-gray-100 text-text-secondary"><X size={18} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div
            ref={dropRef}
            onDragOver={e => e.preventDefault()}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            className={cn(
              'border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors',
              file ? 'border-primary-300 bg-primary-50' : 'border-gray-200 hover:border-gray-300 bg-gray-50'
            )}
          >
            <input ref={fileInputRef} type="file" className="hidden" onChange={handleFile} />
            {file ? (
              <div className="space-y-1">
                <p className="text-2xl">{fileIcon(file.type)}</p>
                <p className="font-medium text-text-primary text-sm">{file.name}</p>
                <p className="text-xs text-text-secondary">{fmtBytes(file.size)}</p>
                <button type="button" onClick={e => { e.stopPropagation(); setFile(null); setNombre(''); }}
                  className="text-xs text-red-500 hover:underline mt-1">Cambiar archivo</button>
              </div>
            ) : (
              <div className="space-y-2">
                <Upload size={24} className="mx-auto text-gray-400" />
                <p className="text-sm font-medium text-text-secondary">Arrastra o haz clic para seleccionar</p>
                <p className="text-xs text-gray-400">PDF, imágenes, Word, audio — máx. 30 MB</p>
              </div>
            )}
          </div>

          <div>
            <label className="block text-xs font-medium text-text-secondary mb-1">Nombre del documento</label>
            <input className={inputCls} value={nombre} onChange={e => setNombre(e.target.value)} placeholder="Nombre descriptivo..." />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-text-secondary mb-1">Categoría</label>
              <select className={inputCls} value={categoria} onChange={e => setCategoria(e.target.value)}>
                {CATEGORIAS.filter(c => c.value).map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-text-secondary mb-1">Descripción</label>
              <input className={inputCls} value={descripcion} onChange={e => setDescripcion(e.target.value)} placeholder="Opcional..." />
            </div>
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 border border-gray-200 text-text-secondary py-2 rounded-lg text-sm hover:bg-gray-50">Cancelar</button>
            <button type="submit" disabled={!file || isPending}
              className="flex-1 bg-primary-600 text-white py-2 rounded-lg text-sm font-medium hover:bg-primary-700 disabled:opacity-60">
              {isPending ? 'Subiendo...' : 'Subir documento'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

interface Props { id: string; canEdit: boolean }

export function DocumentosPacienteTab({ id, canEdit }: Props) {
  const qc = useQueryClient();
  const [uploadOpen, setUploadOpen] = useState(false);
  const [categoriaFiltro, setCategoriaFiltro] = useState('');

  const { data: documentos = [], isLoading } = useQuery({
    queryKey: ['documentos-paciente', id],
    queryFn: () => api.get(`/api/pacientes/${id}/documentos`).then(r => r.data as any[]),
  });

  const uploadMutation = useMutation({
    mutationFn: (fd: FormData) => api.post(`/api/pacientes/${id}/documentos/upload`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then(r => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['documentos-paciente', id] }); toast.success('Documento subido'); setUploadOpen(false); },
    onError: (err: any) => toast.error(getErrorMessage(err, 'Error al subir el documento')),
  });

  const deleteMutation = useMutation({
    mutationFn: (docId: string) => api.delete(`/api/pacientes/${id}/documentos/${docId}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['documentos-paciente', id] }); toast.success('Documento eliminado'); },
  });

  const visibles = categoriaFiltro
    ? documentos.filter((d: any) => d.categoria === categoriaFiltro)
    : documentos;

  function canPreview(mime: string) {
    return mime?.startsWith('image/') || mime === 'application/pdf';
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Filter size={13} className="text-text-secondary" />
          <div className="flex gap-1.5 flex-wrap">
            {CATEGORIAS.map(c => (
              <button key={c.value} type="button"
                onClick={() => setCategoriaFiltro(c.value)}
                className={cn('px-2.5 py-1 rounded-full text-xs font-medium transition-colors border', categoriaFiltro === c.value ? 'bg-primary-600 text-white border-primary-600' : 'border-gray-200 text-text-secondary hover:border-gray-300')}>
                {c.label}
              </button>
            ))}
          </div>
        </div>
        {canEdit && (
          <button type="button" onClick={() => setUploadOpen(true)}
            className="flex items-center gap-1.5 bg-primary-600 text-white px-3 py-1.5 rounded-lg text-xs font-medium hover:bg-primary-700 transition-colors flex-shrink-0">
            <Upload size={13} /> Subir documento
          </button>
        )}
      </div>

      {isLoading ? (
        <p className="text-center py-10 text-sm text-text-secondary">Cargando...</p>
      ) : visibles.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-xl border border-dashed border-gray-200">
          <FolderOpen size={28} className="mx-auto text-gray-300 mb-2" />
          <p className="text-sm text-text-secondary">
            {categoriaFiltro ? `Sin documentos en "${CATEGORIAS.find(c => c.value === categoriaFiltro)?.label}"` : 'Sin documentos registrados'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {visibles.map((d: any) => (
            <div key={d.id} className="bg-white border border-gray-100 rounded-xl p-3.5 flex items-start gap-3 group">
              <div className="text-2xl flex-shrink-0 mt-0.5">{fileIcon(d.tipoArchivo)}</div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-text-primary text-sm truncate" title={d.nombre}>{d.nombre}</p>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className={cn('text-[10px] font-medium border px-1.5 py-0.5 rounded-full capitalize', CATEGORIA_COLOR[d.categoria] || CATEGORIA_COLOR.otro)}>
                    {CATEGORIAS.find(c => c.value === d.categoria)?.label || d.categoria}
                  </span>
                  {d.tamaño && <span className="text-[11px] text-text-secondary">{fmtBytes(Number(d.tamaño))}</span>}
                </div>
                {d.descripcion && <p className="text-xs text-text-secondary mt-1 truncate">{d.descripcion}</p>}
                <p className="text-[11px] text-text-secondary mt-1">{fmtDate(d.createdAt)}</p>
              </div>
              <div className="flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                {canPreview(d.tipoArchivo) && (
                  <button type="button"
                    onClick={() => openS3File(d.archivoUrl).catch((err: any) => toast.error(getErrorMessage(err, 'No se pudo abrir el archivo')))}
                    className="p-1.5 rounded-lg hover:bg-gray-100 text-text-secondary transition-colors" title="Ver">
                    <Eye size={13} />
                  </button>
                )}
                <button type="button"
                  onClick={() => downloadS3File(d.archivoUrl, d.archivoNombre).catch((err: any) => toast.error(getErrorMessage(err, 'No se pudo descargar el archivo')))}
                  className="p-1.5 rounded-lg hover:bg-gray-100 text-text-secondary transition-colors" title="Descargar">
                  <Download size={13} />
                </button>
                {canEdit && (
                  <button type="button" onClick={() => deleteMutation.mutate(d.id)}
                    className="p-1.5 rounded-lg hover:bg-red-50 text-text-secondary hover:text-red-500 transition-colors" title="Eliminar">
                    <X size={13} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {uploadOpen && (
        <UploadModal
          onClose={() => setUploadOpen(false)}
          onUpload={fd => uploadMutation.mutate(fd)}
          isPending={uploadMutation.isPending}
        />
      )}
    </div>
  );
}
